import { useMemo } from 'react'
import {
  Background,
  Handle,
  MarkerType,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
  type Node,
  type NodeProps,
  type NodeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import type { ProvenanceChain, ProvenanceNode } from '@deepguide-ai/types'
import { layout as dagreLayout } from 'dagre-d3-es/src/dagre/index.js'
import { Graph as DagreGraph } from 'dagre-d3-es/src/graphlib/index.js'
import {
  ArrowRight,
  Box,
  FileCheck2,
  FolderTree,
  Link2,
  Maximize2,
  ShieldCheck,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import { Button } from '@deepguide-ai/ui/button'
import { Card, CardDescription, CardHeader, CardTitle } from '@deepguide-ai/ui/card'
import { cn } from '@/lib/utils'
import { AssertionSourceBadge } from './AssertionSourceBadge'

type LineageNodeData = {
  node: ProvenanceNode
  isInteractive: boolean
  isSelected: boolean
}

type LineageFlowNode = Node<LineageNodeData, 'lineage'>

const NODE_WIDTH = 248
const NODE_HEIGHT = 116

const TYPE_META: Record<
  ProvenanceNode['type'],
  {
    label: string
    border: string
    bg: string
    icon: typeof ShieldCheck
  }
> = {
  policy: {
    label: 'Policy',
    border: 'border-indigo-200 dark:border-status-purple/30',
    bg: 'bg-indigo-50/80 dark:bg-status-purple-bg',
    icon: ShieldCheck,
  },
  claim: {
    label: 'Claim',
    border: 'border-amber-200 dark:border-status-orange/30',
    bg: 'bg-amber-50/80 dark:bg-status-orange-bg',
    icon: ShieldCheck,
  },
  test: {
    label: 'Test',
    border: 'border-blue-200 dark:border-status-blue/30',
    bg: 'bg-blue-50/80 dark:bg-status-blue-bg',
    icon: FileCheck2,
  },
  submission: {
    label: 'Submission',
    border: 'border-emerald-200 dark:border-status-green/30',
    bg: 'bg-emerald-50/80 dark:bg-status-green-bg',
    icon: ArrowRight,
  },
  pack: {
    label: 'Evidence Pack',
    border: 'border-slate-200 dark:border-status-gray/30',
    bg: 'bg-slate-50/90 dark:bg-status-gray-bg',
    icon: Box,
  },
  backlink: {
    label: 'Backlink',
    border: 'border-fuchsia-200 dark:border-status-purple/30',
    bg: 'bg-fuchsia-50/80 dark:bg-status-purple-bg',
    icon: Link2,
  },
  control: {
    label: 'Control',
    border: 'border-teal-200 dark:border-status-green/30',
    bg: 'bg-teal-50/80 dark:bg-status-green-bg',
    icon: FolderTree,
  },
}

function relationshipLabel(value: string) {
  return value.replaceAll('_', ' ')
}

function formatTimestamp(value?: string | null) {
  if (!value) return null

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleString()
}

function LineageNodeCard({ data }: NodeProps<LineageFlowNode>) {
  const meta = TYPE_META[data.node.type]
  const Icon = meta.icon

  return (
    <div className='w-[248px]'>
      <Handle type='target' position={Position.Top} className='!h-2 !w-2 !border-0 !bg-slate-300 dark:!bg-status-gray' />
      <div
        className={cn(
          'rounded-md border p-4 shadow-sm transition-colors',
          meta.border,
          meta.bg,
          data.isInteractive && 'cursor-pointer hover:bg-card',
          data.isSelected && 'border-primary bg-card ring-2 ring-primary/20'
        )}
      >
        <div className='flex items-start justify-between gap-3'>
          <div className='flex min-w-0 items-center gap-2'>
            <div className='rounded-md bg-card/90 p-2 shadow-sm'>
              <Icon className='h-4 w-4 text-slate-700 dark:text-status-gray-strong' />
            </div>
            <div className='min-w-0'>
              <div className='text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500 dark:text-text-subtle'>
                {meta.label}
              </div>
              <div className='mt-1 line-clamp-2 text-sm font-semibold text-slate-900 dark:text-text-secondary'>
                {data.node.label}
              </div>
            </div>
          </div>
          <AssertionSourceBadge source={data.node.assertionSource} />
        </div>

        {(data.node.status || data.node.timestamp) && (
          <div className='mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600 dark:text-text-body'>
            {data.node.status && (
              <span className='rounded-full bg-card/80 px-2 py-1 font-medium'>
                {data.node.status}
              </span>
            )}
            {formatTimestamp(data.node.timestamp) && (
              <span>{formatTimestamp(data.node.timestamp)}</span>
            )}
          </div>
        )}
      </div>
      <Handle type='source' position={Position.Bottom} className='!h-2 !w-2 !border-0 !bg-slate-300 dark:!bg-status-gray' />
    </div>
  )
}

const nodeTypes: NodeTypes = {
  lineage: LineageNodeCard,
}

function toReactFlowGraph(chain: ProvenanceChain, selectedNodeId?: string | null) {
  const graph = new DagreGraph()
  graph.setDefaultEdgeLabel(() => ({}))
  graph.setGraph({
    rankdir: 'TB',
    nodesep: 28,
    ranksep: 96,
    marginx: 20,
    marginy: 20,
  })

  const nodes: LineageFlowNode[] = chain.nodes.map((node) => {
    graph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT })

    return {
      id: node.id,
      type: 'lineage',
      position: { x: 0, y: 0 },
      data: {
        node,
        isInteractive:
          node.type === 'test'
          || node.type === 'pack'
          || node.type === 'control',
        isSelected: node.id === selectedNodeId,
      },
      draggable: false,
      selectable: true,
    }
  })

  const edges: Edge[] = chain.edges.map((edge) => {
    graph.setEdge(edge.from, edge.to)

    return {
      id: `${edge.from}-${edge.to}-${edge.relationship}`,
      source: edge.from,
      target: edge.to,
      type: 'smoothstep',
      label: relationshipLabel(edge.relationship),
      labelStyle: {
        fill: 'var(--text-subtle)',
        fontSize: 11,
        fontWeight: 500,
      },
      labelBgStyle: {
        fill: 'var(--card)',
        stroke: 'var(--border)',
        strokeWidth: 1,
      },
      labelBgPadding: [4, 2],
      labelBgBorderRadius: 4,
      style: {
        stroke: 'light-dark(#94a3b8, var(--status-gray))',
        strokeWidth: 1.5,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 16,
        height: 16,
        color: 'light-dark(#94a3b8, var(--status-gray))',
      },
    }
  })

  dagreLayout(graph)

  return {
    nodes: nodes.map((node) => {
      const layoutNode = graph.node(node.id)

      return {
        ...node,
        position: {
          x: layoutNode.x - NODE_WIDTH / 2,
          y: layoutNode.y - NODE_HEIGHT / 2,
        },
      }
    }),
    edges,
  }
}

function LineageZoomControls() {
  const reactFlow = useReactFlow()

  return (
    <Panel position='bottom-left' className='!m-3'>
      <div className='border-border flex items-center gap-0.5 rounded-md border bg-card/95 px-2 py-1.5 shadow-sm backdrop-blur-sm'>
        <Button
          size='icon'
          variant='ghost'
          className='h-7 w-7'
          tooltip='Zoom in'
          aria-label='Zoom in'
          onClick={() => reactFlow.zoomIn()}
        >
          <ZoomIn className='h-3.5 w-3.5' />
        </Button>
        <Button
          size='icon'
          variant='ghost'
          className='h-7 w-7'
          tooltip='Zoom out'
          aria-label='Zoom out'
          onClick={() => reactFlow.zoomOut()}
        >
          <ZoomOut className='h-3.5 w-3.5' />
        </Button>
        <Button
          size='icon'
          variant='ghost'
          className='h-7 w-7'
          tooltip='Fit canvas to screen'
          aria-label='Fit canvas to screen'
          onClick={() => reactFlow.fitView()}
        >
          <Maximize2 className='h-3.5 w-3.5' />
        </Button>
      </div>
    </Panel>
  )
}

function LineageViewerInner({
  chain,
  workspaceId: _workspaceId,
  emptyStateTitle,
  emptyStateDescription,
  className,
  selectedNodeId,
  onSelectNode,
}: {
  chain: ProvenanceChain
  workspaceId: string
  emptyStateTitle?: string
  emptyStateDescription?: string
  className?: string
  selectedNodeId?: string
  onSelectNode?: (node: ProvenanceNode) => void
}) {
  const { nodes, edges } = useMemo(
    () => toReactFlowGraph(chain, selectedNodeId),
    [chain, selectedNodeId]
  )

  if (chain.nodes.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>{emptyStateTitle ?? 'No lineage available'}</CardTitle>
          <CardDescription>
            {emptyStateDescription ?? 'This record does not have a provenance chain yet.'}
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <div className={className}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.18 }}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        panOnDrag
        onNodeClick={(_, flowNode) => {
          const node = flowNode.data?.node
          if (!node) return
          onSelectNode?.(node)

          // Navigation is intentionally disabled while the dialog uses node
          // selection to drive the right-hand details panel.
          //
          // Previous targets:
          // - test -> /workspaces/$workspaceId/tests/$testId
          // - pack -> /workspaces/$workspaceId/evidence-viewer/$evidencePackId
          // - control -> /workspaces/$workspaceId/controls/$controlDefId
        }}
        className='rounded-md bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.95),_rgba(240,246,252,0.95))] dark:bg-[radial-gradient(circle_at_top_left,_var(--card),_var(--background))]'
      >
        <Background
          gap={18}
          size={1}
          color='light-dark(#dbe4ee, var(--border))'
        />
        <LineageZoomControls />
      </ReactFlow>
    </div>
  )
}

export function LineageViewer(props: {
  chain: ProvenanceChain
  workspaceId: string
  emptyStateTitle?: string
  emptyStateDescription?: string
  className?: string
  selectedNodeId?: string
  onSelectNode?: (node: ProvenanceNode) => void
}) {
  return (
    <ReactFlowProvider>
      <LineageViewerInner {...props} />
    </ReactFlowProvider>
  )
}
